import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Check, Phone, MessageSquare, Clock, Zap, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState({ state: "verifying", data: null });
  const [config, setConfig] = useState({ chi_phone: "", turnaround_hours: 4 });
  const [intake, setIntake] = useState({
    website: "",
    instagram: "",
    facebook: "",
    phone: "",
    target_customer: "",
    promo_ideas: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Load public config (Chi's phone + turnaround)
  useEffect(() => {
    axios.get(`${API}/config/public`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  // Poll Stripe status until paid (or max attempts)
  const pollStatus = useCallback(
    async (attempts = 0) => {
      if (!sessionId) {
        setStatus({ state: "missing", data: null });
        return;
      }
      if (attempts >= 12) {
        setStatus((s) =>
          s.state === "paid"
            ? s
            : { state: "pending", data: s.data, message: "Payment is taking a moment. Check your email for the receipt — Chi will reach out shortly." }
        );
        return;
      }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const d = res.data;
        if (d.payment_status === "paid") {
          setStatus({ state: "paid", data: d });
          return;
        }
        if (d.status === "expired") {
          setStatus({ state: "expired", data: d });
          return;
        }
        setStatus({ state: "pending", data: d });
        setTimeout(() => pollStatus(attempts + 1), 2000);
      } catch (e) {
        setStatus({ state: "error", data: null });
      }
    },
    [sessionId]
  );

  useEffect(() => {
    pollStatus(0);
  }, [pollStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/intake`, { session_id: sessionId, ...intake });
      setSubmitted(true);
      toast.success("Got it! Chi has your details.");
    } catch (err) {
      toast.error("Could not submit — please text Chi directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k) => (e) => setIntake({ ...intake, [k]: e.target.value });

  // Build SMS link to Chi
  const customerName = status.data?.customer_name || "";
  const business = status.data?.business || "";
  const pkgName = status.data?.package_name || "Spurs Finals Cash-In";
  const smsBody = encodeURIComponent(
    `Hi Chi! I just claimed my Finals Spot 🏀 — ${pkgName}. Ready to roll!`
  );
  const phoneDigits = (config.chi_phone || "").replace(/\D/g, "");
  const smsHref = phoneDigits ? `sms:+1${phoneDigits}?&body=${smsBody}` : "#";
  const telHref = phoneDigits ? `tel:+1${phoneDigits}` : "#";
  const prettyPhone = phoneDigits
    ? `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`
    : "";

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] antialiased">
      <Toaster position="top-center" richColors />

      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 hover:text-[#0A0A0A] transition mb-10"
        >
          <ArrowLeft size={14} /> Back to Haus of Chi
        </Link>

        {/* Confirmation hero */}
        <div className="relative overflow-hidden rounded-[2rem] bg-[#0A0A0A] text-white p-8 md:p-14">
          <div className="pointer-events-none absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-[#FF8200]/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 w-[400px] h-[400px] rounded-full bg-[#00B2A9]/20 blur-3xl" />

          <div className="relative">
            {status.state === "paid" ? (
              <div className="inline-flex items-center gap-2 bg-[#00B2A9] text-white text-[11px] md:text-xs font-bold tracking-[0.2em] uppercase py-2 px-4 rounded-full mb-6">
                <Check size={14} strokeWidth={3} /> Payment Confirmed
              </div>
            ) : status.state === "pending" || status.state === "verifying" ? (
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-[11px] md:text-xs font-bold tracking-[0.2em] uppercase py-2 px-4 rounded-full mb-6">
                <span className="w-2 h-2 rounded-full bg-[#FF8200] animate-pulse" />
                Verifying payment…
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 bg-[#EF426F] text-white text-[11px] md:text-xs font-bold tracking-[0.2em] uppercase py-2 px-4 rounded-full mb-6">
                Heads up
              </div>
            )}

            <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] max-w-3xl">
              {status.state === "paid" ? (
                <>
                  You're{" "}
                  <span className="bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-transparent bg-clip-text">
                    on the roster.
                  </span>{" "}
                  🏀
                </>
              ) : (
                <>
                  Almost there —{" "}
                  <span className="bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-transparent bg-clip-text">
                    hang tight
                  </span>
                  .
                </>
              )}
            </h1>

            {status.state === "paid" && (
              <p className="mt-6 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed">
                {customerName && (
                  <>
                    Thank you, <span className="font-bold text-white">{customerName}</span>.{" "}
                  </>
                )}
                Your <span className="font-bold text-white">{pkgName}</span> is locked in
                {business && (
                  <>
                    {" "}
                    for <span className="font-bold text-white">{business}</span>
                  </>
                )}
                .
              </p>
            )}

            {/* Turnaround promise */}
            <div className="mt-8 inline-flex items-center gap-3 bg-white/10 border border-white/20 rounded-2xl px-5 py-4 backdrop-blur">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00B2A9] via-[#EF426F] to-[#FF8200] flex items-center justify-center">
                <Zap size={20} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-zinc-400">
                  Quick turnaround
                </div>
                <div className="text-base md:text-lg font-black tracking-tight">
                  Created & complete in less than {config.turnaround_hours} hours
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Two-column: Text Chi + Intake form */}
        <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Text Chi card */}
          <div className="lg:col-span-5 bg-gradient-to-br from-[#00B2A9]/10 via-[#EF426F]/10 to-[#FF8200]/10 border border-black/5 rounded-3xl p-7 md:p-8 flex flex-col">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-white shadow-sm">
              <Clock size={12} strokeWidth={3} className="text-[#EF426F]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em]">
                Fastest Path
              </span>
            </div>
            <h2 className="mt-5 text-2xl md:text-3xl font-black tracking-tight leading-tight">
              Text Chi directly.
            </h2>
            <p className="mt-3 text-sm md:text-base text-zinc-600 leading-relaxed">
              Skip the form — shoot Chi a quick text and get started immediately.
              Reply with your business name and what you need first.
            </p>
            <a
              href={smsHref}
              data-testid="success-text-chi"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0A] text-white font-bold py-4 px-6 text-sm tracking-wider uppercase transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <MessageSquare size={16} strokeWidth={2.5} />
              Text Chi · {prettyPhone}
            </a>
            <a
              href={telHref}
              data-testid="success-call-chi"
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-white border border-black/10 text-[#0A0A0A] font-bold py-3.5 px-6 text-sm tracking-wider uppercase transition-all hover:bg-zinc-50"
            >
              <Phone size={14} strokeWidth={2.5} /> Or call instead
            </a>
            <p className="mt-5 text-[11px] text-zinc-500 text-center uppercase tracking-[0.2em] font-bold">
              ⚡ Less than {config.turnaround_hours}-hour turnaround
            </p>
          </div>

          {/* Intake form */}
          <div className="lg:col-span-7 bg-white border border-black/5 rounded-3xl p-7 md:p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF8200]">
              Or — drop your details below
            </p>
            <h2 className="mt-2 text-2xl md:text-3xl font-black tracking-tight leading-tight">
              Quick intake form
            </h2>
            <p className="mt-2 text-sm text-zinc-600">
              30 seconds. Helps Chi get started without back-and-forth.
            </p>

            {submitted ? (
              <div className="mt-8 rounded-2xl bg-gradient-to-r from-[#00B2A9]/10 via-[#EF426F]/10 to-[#FF8200]/10 border border-black/5 p-6 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-[#00B2A9] to-[#FF8200] flex items-center justify-center mb-3">
                  <Check size={22} className="text-white" strokeWidth={3} />
                </div>
                <h3 className="text-xl font-black tracking-tight">Got it!</h3>
                <p className="mt-2 text-sm text-zinc-600">
                  Chi has your details and will be in touch within the next{" "}
                  {config.turnaround_hours} hours. Watch your email and phone.
                </p>
                <a
                  href={smsHref}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#EF426F] hover:underline"
                >
                  <MessageSquare size={14} /> Still want to text Chi? Tap here
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 space-y-3.5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  <Field label="Website" value={intake.website} onChange={update("website")} placeholder="https://yourbusiness.com" testId="intake-website" />
                  <Field label="Best phone" value={intake.phone} onChange={update("phone")} placeholder="(210) 555-0100" testId="intake-phone" />
                  <Field label="Instagram" value={intake.instagram} onChange={update("instagram")} placeholder="@yourbusiness" testId="intake-instagram" />
                  <Field label="Facebook" value={intake.facebook} onChange={update("facebook")} placeholder="facebook.com/yourbusiness" testId="intake-facebook" />
                </div>
                <Field
                  label="Who's your ideal customer?"
                  value={intake.target_customer}
                  onChange={update("target_customer")}
                  placeholder="e.g. Spurs fans in their 20-40s craving game-day food"
                  testId="intake-target"
                />
                <TextArea
                  label="Any Finals promo ideas?"
                  value={intake.promo_ideas}
                  onChange={update("promo_ideas")}
                  placeholder="Discounts, watch parties, deals — anything you already have in mind."
                  testId="intake-promos"
                />
                <TextArea
                  label="Anything else Chi should know?"
                  value={intake.notes}
                  onChange={update("notes")}
                  placeholder="Brand colors, voice, exclusions, deadlines, etc."
                  testId="intake-notes"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="intake-submit"
                  className="w-full mt-2 rounded-full bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-white font-bold py-4 text-sm tracking-wider uppercase transition-all hover:opacity-95 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting…" : "🏀 Send to Chi"}
                </button>
                <p className="text-[11px] text-center text-zinc-500 pt-1">
                  Chi will deliver your assets in less than {config.turnaround_hours} hours.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Session ref */}
        {sessionId && (
          <p className="mt-10 text-[11px] text-zinc-400 text-center font-mono">
            Order ref: {sessionId.slice(0, 18)}…
          </p>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-700 mb-1.5">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#00B2A9]"
    />
  </div>
);

const TextArea = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-700 mb-1.5">
      {label}
    </label>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      rows={3}
      className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#00B2A9] resize-none"
    />
  </div>
);

export default SuccessPage;
